import { useState } from 'react';
import moment from 'moment';
import { Event, RoomType } from '../services/Types';
import { availableSlotRoomIds, bookMultipleRooms } from '../services/EventService';
import { convertMinutesIntoTime } from '../services/Helper';

interface RoomConfig {
  overHundredParticipants: boolean;
}

interface AvailabilityResult {
  allRoomsAvailable: boolean;
  availableRoomIds: number[];
}

interface SchedulingResult {
  availableRoomIds?: number[];
  error?: string;
}

export const useRoomScheduling = (
  event: Event,
  startTime: number,
  endTime: number,
  selectedDate: Date | null,
  adminBooking: boolean = false
) => {
  const [isBooking, setIsBooking] = useState(false);

  const checkRoomAvailability = async (
    roomConfigs: RoomConfig[]
  ): Promise<AvailabilityResult> => {
    if (!selectedDate) {
      return { allRoomsAvailable: false, availableRoomIds: [] };
    }

    const selectedSlot = convertMinutesIntoTime(startTime);
    const duration = endTime - startTime;
    const availableRoomIds: number[] = [];

    // Group room configurations by audience to avoid duplicate API calls
    const roomConfigsByAudience = new Map<number, number>();
    for (const config of roomConfigs) {
      const audience = config.overHundredParticipants ? 3000 : 500;
      roomConfigsByAudience.set(audience, (roomConfigsByAudience.get(audience) || 0) + 1);
    }

    // Make one API call per unique audience type
    for (const [audience, count] of Array.from(roomConfigsByAudience.entries())) {
      const input = {
        duration: duration,
        liveBookingDate: moment(selectedDate).format('YYYY-MM-DD'),
        dryBookingDate: moment(selectedDate).format('YYYY-MM-DD'),
        audience: audience,
        category: 'live'
      };

      try {
        const { data } = await availableSlotRoomIds(selectedSlot, event.id || 0, event.eventType, input, adminBooking);
        if (data?.availableSlotRoomIds?.statusCode === 200 && data.availableSlotRoomIds.data.length >= count) {
          // Grab the list of available rooms for this audience type
          const roomsForThisAudience = data.availableSlotRoomIds.data.slice(0, count);
          availableRoomIds.push(...roomsForThisAudience);
        } else {
          return { allRoomsAvailable: false, availableRoomIds: [] };
        }
      } catch (error) {
        console.error('Error checking room availability:', error);
        return { allRoomsAvailable: false, availableRoomIds: [] };
      }
    }

    return { allRoomsAvailable: true, availableRoomIds };
  };

  const createRoomBooking = async (
    eventId: number,
    availableRoomIds: number[]
  ): Promise<string | null> => {
    if (!selectedDate) {
      return 'Date is missing';
    }

    if (!eventId) {
      return 'Event ID not found';
    }

    setIsBooking(true);
    
    try {
      // Prepare all room bookings
      const bookings = availableRoomIds.map((roomId) => ({
        roomId: roomId,
        date: moment(selectedDate).format('YYYY-MM-DD'),
        startTime: startTime,
        endTime: endTime,
        category: 'live',
        type: 'event'
      }));

      // Book all rooms for the single event
      const { data } = await bookMultipleRooms(eventId, bookings);
      if (data?.bookMultipleRooms?.statusCode === 200) {
        return null; // Success - no error
      } else {
        return 'Event saved but error booking rooms';
      }
    } catch (error) {
      console.error('Error booking multiple rooms:', error);
      return 'Error booking rooms';
    } finally {
      setIsBooking(false);
    }
  };

  const getRoomConfigs = (roomSelected: RoomType): RoomConfig[] => {
    switch (roomSelected) {
      case RoomType.smallRoom:
        return [{ overHundredParticipants: false }];
      case RoomType.largeRoom:
        return [{ overHundredParticipants: true }];
      case RoomType.bothSmallRooms:
        return [
          { overHundredParticipants: false },
          { overHundredParticipants: false }
        ];
      case RoomType.allRooms:
        return [
          { overHundredParticipants: false },
          { overHundredParticipants: false },
          { overHundredParticipants: true }
        ];
      default:
        return [];
    }
  };

  const checkMultipleAvailability = async (roomSelected: RoomType): Promise<SchedulingResult> => {
    const roomConfigs = getRoomConfigs(roomSelected);
    
    if (roomConfigs.length === 0) {
      return { error: 'Invalid room selection' };
    }

    const { allRoomsAvailable, availableRoomIds } = await checkRoomAvailability(roomConfigs);
    
    if (!allRoomsAvailable) {
      return { error: 'Not all selected rooms are available for the specified time' };
    }

    return { availableRoomIds };
  };

  return {
    createRoomBooking,
    checkMultipleAvailability,
    isBooking
  };
}; 